// The page-scoped state behind artifact version history (#11819, #12187).
//
// It sits in its own module so both owners can read it without importing each
// other: `artifactHistory.ts` drives the open/close semantics, and
// `FpPages/viewerMode.ts` reads the presentation override. The `ViewerMode`
// import is type-only, so nothing here depends on that module at runtime.
import { create } from "zustand";

import type { ViewerMode } from "../FpPages/viewerMode";
import type { CommentDraft } from "../FpEditor/services/documentCommentsBridge";

/** The side columns an artifact pane can open beside its content; one at a
 *  time (#13194: comments joined version history). */
export type ArtifactSideColumn = "history" | "comments";

export interface ArtifactHistoryState {
  /**
   * The comments column's active thread and the comment being written
   * (#13194). Here rather than in the pane, for the reason `openFor` is:
   * the pane can re-mount in the other host while comments are open (the
   * viewer mode switched by hand, or comments opened while version history
   * holds the document in the overlay and then closed, which hands it back),
   * and the active thread, or the selection the composer quotes, must
   * survive the move.
   */
  activeComment: string | null;
  commentDraft: CommentDraft | null;
  /**
   * The `?comment=` link this page has already honoured. A link opens the
   * column once per history entry (the router's location key), whichever
   * document's pane mounts under it: a pane mounted afresh with the link
   * still in the address (a move between side-by-side and the overlay), and
   * the Artifacts page opening another document with a raw
   * `pushState` the router never sees, so its pane mounts under the same
   * entry too. The artifact and the thread say which thread the link makes
   * active once that artifact's list has loaded; `activated` records that it
   * has been (or that closing the column ended the link's business first).
   */
  honouredLink: {
    locationKey: string;
    artifactId: string;
    threadId: string;
    activated: boolean;
  } | null;
  /** The artifact whose side column is open, if any. */
  openFor: string | null;
  /** Which column is open for `openFor`. */
  column: ArtifactSideColumn;
  /**
   * Whether opening history moved the pane into the overlay — only then does
   * closing it hand the pane back.
   */
  presentedInOverlay: boolean;
  /**
   * Mounted hosts of the artifact whose history is open (the pane and its
   * header cluster each count). A host unmounting is a real close only when
   * no host is left after the commit — the hand-off unmounts one host and
   * mounts another in the same commit.
   */
  hosts: number;
  /**
   * A transient presentation override of the artifact viewer mode: history
   * shows the artifact in the overlay for as long as it is open, without
   * touching the remembered preference. Deliberately not storage — it must
   * not survive a reload, and clearing it is what hands the pane back.
   */
  artifactModeOverride: ViewerMode | null;
  /**
   * The artifact a chat opened from an artifact's Chat (#13261), and the
   * route it opened on. The pane on that route does not offer Chat again:
   * the reader is already in the chat it would open. Page-scoped like the
   * rest: the open outlives the pane that consumed the hand-off.
   */
  chatHandoff: { artifactId: string; pathname: string } | null;
  /**
   * The artifact Layout's side-by-side pane shows, if any (#13261): a Chat
   * hand-off of that same artifact keeps the pane rather than opening it a
   * second time.
   */
  paneArtifactId: string | null;
}

/**
 * Page-scoped by design: "history is open for artifact X" outlives any one
 * pane component, because the split→overlay hand-off re-mounts the pane in
 * the other host. A store rather than module `let`s so the state has an owner
 * the story harness resets between stories (#12187).
 */
export const useArtifactHistoryStore = create<ArtifactHistoryState>()(() => ({
  openFor: null,
  column: "history",
  activeComment: null,
  commentDraft: null,
  honouredLink: null,
  presentedInOverlay: false,
  hosts: 0,
  artifactModeOverride: null,
  chatHandoff: null,
  paneArtifactId: null,
}));
